import { useCallback, useState } from 'react'
import { systemPipeline, NOT_STARTED_STAGE } from 'app/components/action-center/systemPipelines'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import type {
  ReconcileBoardResult,
  ReconcileContact,
  UseOpsPipelineReconcileReturn,
} from 'app/types/system-pipeline-reconcile'
import { useWorkspace } from 'app/utils'
import { candidateSearch, planForPipeline, reconcilablePipelineIds } from 'lib/systemPipelineReconcile'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { OPS_RECONCILE_ADD_MUTATION, OPS_RECONCILE_CANDIDATES_QUERY } from './graphql'

const PAGE_SIZE = 200
const WRITE_CONCURRENCY = 8
/** Braselton's widest board reads ~2.4k rows, so 20k is headroom, not a working limit. */
const MAX_PAGES = 100

async function fetchCandidates(pipelineId: string): Promise<ReconcileContact[]> {
  const search = candidateSearch(pipelineId)
  const all: ReconcileContact[] = []
  for (let page = 0; ; page++) {
    if (page >= MAX_PAGES) throw new Error(`${pipelineId}: candidate search exceeded ${MAX_PAGES * PAGE_SIZE} rows`)
    const { data, error } = await gqlQuery<{ contacts: { hits: ReconcileContact[] } }>(
      OPS_RECONCILE_CANDIDATES_QUERY,
      { search, pagination: { limit: PAGE_SIZE, page } },
      // Scan is a re-read by definition and its variables never vary, so on the default cache-first
      // policy every scan after the first would replay the first one's response without touching the
      // network. A drift detector reporting a cached snapshot is wrong by construction.
      { requestPolicy: `network-only` },
    )
    if (error) throw new Error(error.message)
    const hits = data?.contacts.hits ?? []
    all.push(...hits)
    if (hits.length < PAGE_SIZE) break
  }
  return all
}

export function useOpsPipelineReconcile(): UseOpsPipelineReconcileReturn {
  const workspace = useWorkspace()
  const [scanning, setScanning] = useState(false)
  const [applying, setApplying] = useState(false)
  const [results, setResults] = useState<ReconcileBoardResult[] | undefined>(undefined)
  const [applied, setApplied] = useState<{ added: number; failed: number } | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  const scan = useCallback(() => {
    setScanning(true)
    setError(undefined)
    // Results are dropped as the scan starts, not merely on failure. They name specific contacts to
    // write to, so a failed re-scan that left the previous set standing would keep an enabled "Add
    // missing" pointed at candidates the server may no longer agree with.
    setResults(undefined)
    bg(async () => {
      try {
        const boards: ReconcileBoardResult[] = []
        for (const pipelineId of reconcilablePipelineIds()) {
          const contacts = await fetchCandidates(pipelineId)
          const plan = planForPipeline(pipelineId, contacts)
          boards.push({
            pipelineId,
            name: systemPipeline(pipelineId)?.name ?? pipelineId,
            examined: contacts.length,
            ok: plan.ok,
            add: plan.add,
            stale: plan.stale,
          })
        }
        setResults(boards)
      } catch (caught) {
        logError(`Ops pipeline reconcile scan failed`, caught)
        setError(caught instanceof Error ? caught.message : String(caught))
      } finally {
        setScanning(false)
      }
    })
  }, [])

  const applyFixes = useCallback(() => {
    if (!results) return
    setApplying(true)
    setError(undefined)
    // Cleared here, not in `scan`: applying ends by re-scanning, and clearing it there wiped the
    // "Added N" summary before anyone could read it.
    setApplied(undefined)
    bg(async () => {
      let added = 0
      let failed = 0
      try {
        for (const board of results) {
          const stage = systemPipeline(board.pipelineId)?.stages[0]?.name ?? NOT_STARTED_STAGE
          for (let i = 0; i < board.add.length; i += WRITE_CONCURRENCY) {
            const chunk = board.add.slice(i, i + WRITE_CONCURRENCY)
            const settled = await Promise.all(
              chunk.map(async (contact) => {
                const { error } = await gqlMutation(OPS_RECONCILE_ADD_MUTATION, {
                  id: contact.id,
                  pipeline: { id: board.pipelineId, stage },
                })
                if (error) logError(`Ops pipeline reconcile failed for ${contact.id}`, error)
                return !error
              }),
            )
            added += settled.filter(Boolean).length
            failed += settled.filter((ok) => !ok).length
          }
        }
        setApplied({ added, failed })
      } finally {
        setApplying(false)
        // Re-read rather than trusting the counts we just wrote: the board is the source of truth.
        scan()
      }
    })
  }, [results, scan])

  return {
    workspaceName: workspace?.name ?? undefined,
    scanning,
    applying,
    results,
    error,
    applied,
    scan,
    applyFixes,
  }
}
